import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import confetti from "canvas-confetti";
import { motion } from "framer-motion";
import { ShoppingBag, MessageCircle, Loader2, Sparkles, CheckCircle2, ArrowRight, Gift, Heart, X, ExternalLink, Package } from "lucide-react";
import { api, formatINR, getGuestId, sameOriginMedia } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { useSettings } from "@/context/SettingsContext";
import { useContent, fillPlaceholders } from "@/lib/content";
import { SectionHeading } from "@/components/wp/SectionHeading";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";

function fireConfetti() {
  const colors = ["#7DA083", "#2E4A3B", "#F5F2E9", "#D4AF37", "#CDBFA8"];
  confetti({ particleCount: 110, spread: 75, origin: { y: 0.6 }, colors });
  setTimeout(() => confetti({ particleCount: 60, angle: 60, spread: 55, origin: { x: 0 }, colors }), 200);
  setTimeout(() => confetti({ particleCount: 60, angle: 120, spread: 55, origin: { x: 1 }, colors }), 200);
}

export default function Checkout() {
  const navigate = useNavigate();
  const { items, subtotal, clear } = useCart();
  const settings = useSettings();
  const content = useContent();
  const shipping = Number(settings?.shipping_charge ?? 85);
  const standardMin = Number(settings?.min_order_value ?? 449);
  const signatureMin = Number(settings?.signature_min_order_value ?? 999);
  const [packaging, setPackaging] = useState("standard");
  const effectiveMin = packaging === "signature" ? signatureMin : standardMin;
  const total = subtotal + shipping;
  const belowMin = subtotal < effectiveMin;
  const amountToMin = Math.max(0, effectiveMin - subtotal);
  const signatureUnlocked = subtotal >= signatureMin;
  const [form, setForm] = useState({ name: "", phone: "", email: "", address: "", city: "", pincode: "", delivery_date: "" });
  const [giftMessage, setGiftMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [order, setOrder] = useState(null);
  const [personalization, setPersonalization] = useState(null);

  useEffect(() => {
    try {
      const raw = localStorage.getItem("wp_personalization");
      if (raw) setPersonalization(JSON.parse(raw));
    } catch { /* noop */ }
  }, []);

  const removePersonalization = () => {
    localStorage.removeItem("wp_personalization");
    setPersonalization(null);
  };

  const genMessage = async () => {
    setAiLoading(true);
    try { const { data } = await api.post("/ai/message", { recipient: "", occasion: "", tone: "heartfelt" }); setGiftMessage(data.message); toast.success("Note added 💝"); }
    catch { toast.error("We couldn't write a note just now."); }
    finally { setAiLoading(false); }
  };

  const placeOrder = async () => {
    if (order || loading) return; // prevent duplicate orders
    if (!form.name || !form.phone) return toast.error("Please add your name and phone.");
    if (belowMin) return toast.error(packaging === "signature" ? `Add ${formatINR(amountToMin)} more to unlock Signature packaging.` : `Add ${formatINR(amountToMin)} more to place your order.`);
    setLoading(true);
    try {
      const payload = {
        guest_id: getGuestId(),
        items: items.map((it) => ({ id: it.id, name: it.name, price: it.price, qty: it.qty, image: it.image, type: it.type })),
        customer: form, gift_message: giftMessage, subtotal, shipping, total, packaging,
        personalization_token: personalization?.token || "",
        gift_url: personalization?.gift_url || "",
      };
      const { data } = await api.post("/orders", payload);
      setOrder(data);
      fireConfetti();
      clear();
      localStorage.removeItem("wp_personalization");
      // Order is safely saved — now attempt the WhatsApp handoff automatically.
      if (data.whatsapp_url) {
        try { window.open(data.whatsapp_url, "_blank", "noopener"); } catch { /* fallback button remains */ }
      }
    } catch (err) { toast.error(err?.response?.data?.detail || "Could not place order. Please try again."); }
    finally { setLoading(false); }
  };

  /* SUCCESS STATE */
  if (order) {
    const ref = order.id.slice(0, 8).toUpperCase();
    return (
      <div className="wp-container py-16" data-testid="checkout-success-state">
        <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
          className="mx-auto max-w-lg rounded-wp-xl border border-border bg-card p-8 text-center shadow-wp-card">
          <div className="mx-auto grid h-20 w-20 place-items-center rounded-full bg-wp-success/15 text-wp-success"><CheckCircle2 className="h-10 w-10" /></div>
          <h2 className="mt-5 font-display text-3xl">Your order request is in! 🎁</h2>
          <p className="mt-1 text-sm font-medium text-wp-ink">Order <span className="font-semibold">#{ref}</span></p>
          <p className="mt-3 text-wp-ink-muted">We've received your order request. We'll confirm your order on WhatsApp and share the payment details. Once payment is confirmed, we'll start preparing your parcel.</p>
          <a href={order.whatsapp_url} target="_blank" rel="noreferrer" data-testid="checkout-whatsapp-confirm-button"
            className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-xl bg-wp-success px-6 py-4 text-base font-semibold text-white shadow-wp-soft hover:brightness-105">
            <MessageCircle className="h-5 w-5" /> Continue on WhatsApp
          </a>
          <Button variant="outline" onClick={() => navigate("/shop")} className="mt-3 w-full rounded-xl">Continue shopping</Button>
        </motion.div>
      </div>
    );
  }

  /* EMPTY CART */
  if (items.length === 0) {
    return (
      <div className="wp-container py-20 text-center">
        <div className="mx-auto grid h-20 w-20 place-items-center rounded-full bg-wp-blush-100 text-wp-rose-strong"><ShoppingBag className="h-9 w-9" /></div>
        <h2 className="mt-4 font-display text-2xl">Your cart is empty</h2>
        <p className="mt-1 text-wp-ink-muted">Add a gift-ready parcel to get started.</p>
        <Button onClick={() => navigate("/shop")} className="mt-5 rounded-xl bg-wp-rose-strong text-white">Shop Gift Boxes <ArrowRight className="ml-1 h-4 w-4" /></Button>
      </div>
    );
  }

  return (
    <div className="wp-container py-10">
      <SectionHeading eyebrow="Almost there" title="Checkout" subtitle="Tell us where to send it — confirm on WhatsApp in seconds. Payments handled in chat." />
      <div className="mt-8 grid gap-8 lg:grid-cols-[1fr_22rem]">
        {/* form */}
        <form className="space-y-4" data-testid="checkout-form" onSubmit={(e) => { e.preventDefault(); placeOrder(); }}>
          {/* Personalization attachment */}
          {personalization ? (
            <div className="flex items-center gap-3 rounded-wp-lg border border-wp-mint/50 bg-wp-mint/10 p-4" data-testid="checkout-personalization-attached">
              <span className="grid h-11 w-11 flex-shrink-0 place-items-center rounded-full bg-white text-wp-rose-strong shadow-wp-soft"><Heart className="h-5 w-5" /></span>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold text-wp-ink">Personal gift page attached{personalization.recipient_name ? ` for ${personalization.recipient_name}` : ""}</p>
                <a href={personalization.gift_url} target="_blank" rel="noreferrer" className="mt-0.5 inline-flex items-center gap-1 text-xs font-medium text-wp-ocean hover:underline">
                  Preview gift page <ExternalLink className="h-3 w-3" />
                </a>
              </div>
              <button type="button" onClick={removePersonalization} aria-label="Remove personalization" data-testid="checkout-remove-personalization" className="grid h-8 w-8 flex-shrink-0 place-items-center rounded-full text-wp-ink-muted hover:bg-white hover:text-wp-rose-strong"><X className="h-4 w-4" /></button>
            </div>
          ) : (
            <Link to="/personalize" data-testid="checkout-add-personalization" className="flex items-center gap-3 rounded-wp-lg border border-dashed border-border bg-wp-blush-50 p-4 transition-colors hover:border-wp-rose-strong">
              <span className="grid h-11 w-11 flex-shrink-0 place-items-center rounded-full bg-white text-wp-rose-strong shadow-wp-soft"><Sparkles className="h-5 w-5" /></span>
              <div className="flex-1">
                <p className="text-sm font-semibold text-wp-ink">Make it personal</p>
                <p className="text-xs text-wp-ink-muted">Add a photo, a voice note &amp; a heartfelt message they can open with a quick scan.</p>
              </div>
              <ArrowRight className="h-5 w-5 text-wp-rose-strong" />
            </Link>
          )}
          {/* Packaging choice */}
          <div data-testid="checkout-packaging">
            <p className="mb-2 text-sm font-semibold text-wp-ink">Choose your packaging</p>
            <div className="grid gap-3 sm:grid-cols-2">
              <button type="button" onClick={() => setPackaging("standard")} data-testid="packaging-standard"
                className={`rounded-wp-lg border p-4 text-left transition-colors ${packaging === "standard" ? "border-wp-rose-strong bg-wp-blush-50 ring-1 ring-wp-rose-strong/30" : "border-border bg-card hover:border-wp-rose-strong/40"}`}>
                <div className="flex items-center gap-2">
                  <Package className="h-4 w-4 text-wp-rose-strong" />
                  <span className="text-sm font-semibold">Standard Gift Packaging</span>
                  {packaging === "standard" && <CheckCircle2 className="ml-auto h-4 w-4 text-wp-success" />}
                </div>
                <p className="mt-1.5 text-xs text-wp-ink-muted">{content.standard_packaging_desc}</p>
              </button>
              <button type="button" onClick={() => setPackaging("signature")} data-testid="packaging-signature"
                className={`rounded-wp-lg border p-4 text-left transition-colors ${packaging === "signature" ? "border-wp-rose-strong bg-wp-blush-50 ring-1 ring-wp-rose-strong/30" : "border-border bg-card hover:border-wp-rose-strong/40"}`}>
                <div className="flex items-center gap-2">
                  <Sparkles className="h-4 w-4 text-wp-gold" />
                  <span className="text-sm font-semibold">Signature Wonder Parcel Box</span>
                  {packaging === "signature" && <CheckCircle2 className="ml-auto h-4 w-4 text-wp-success" />}
                </div>
                <p className="mt-1.5 text-xs text-wp-ink-muted" data-testid="packaging-signature-hint">
                  {signatureUnlocked ? content.signature_packaging_desc : `Unlock our Signature Wonder Parcel Box with parcels ${formatINR(signatureMin)}+`}
                </p>
              </button>
            </div>
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <Input placeholder="Full name*" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="checkout-name" className="rounded-wp-md" />
            <Input placeholder="Phone (WhatsApp)*" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="checkout-phone" className="rounded-wp-md" />
          </div>
          <Input placeholder="Email (optional)" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="rounded-wp-md" />
          <Textarea placeholder="Delivery address" rows={2} value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} data-testid="checkout-address" className="rounded-wp-md" />
          <div className="grid gap-4 sm:grid-cols-3">
            <Input placeholder="City" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} className="rounded-wp-md" />
            <Input placeholder="Pincode" value={form.pincode} onChange={(e) => setForm({ ...form, pincode: e.target.value })} className="rounded-wp-md" />
            <Input type="date" value={form.delivery_date} onChange={(e) => setForm({ ...form, delivery_date: e.target.value })} className="rounded-wp-md" />
          </div>
          <div>
            <div className="mb-1.5 flex items-center justify-between">
              <label className="text-sm font-medium">Gift note</label>
              <button type="button" onClick={genMessage} disabled={aiLoading} className="flex items-center gap-1 text-xs font-medium text-wp-ocean hover:underline" data-testid="checkout-ai-message-button">
                {aiLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />} Write it for me
              </button>
            </div>
            <Textarea placeholder="Add a sweet message we’ll handwrite on a card…" rows={3} value={giftMessage} onChange={(e) => setGiftMessage(e.target.value)} data-testid="checkout-gift-message" className="rounded-wp-md" />
          </div>
        </form>

        {/* summary */}
        <div className="lg:sticky lg:top-20 lg:self-start">
          <div className="rounded-wp-lg border border-border bg-card p-5 shadow-wp-card">
            <h3 className="flex items-center gap-2 font-display text-xl"><Gift className="h-5 w-5 text-wp-rose-strong" /> Order summary</h3>
            <ul className="mt-4 space-y-3">
              {items.map((it) => (
                <li key={`${it.type}-${it.id}`} className="flex items-center gap-3">
                  <div className="h-12 w-12 overflow-hidden rounded-wp-sm bg-wp-cream-100">{it.image && <img src={sameOriginMedia(it.image)} alt={it.name} loading="lazy" className="h-full w-full object-cover" />}</div>
                  <div className="flex-1"><p className="line-clamp-1 text-sm font-medium">{it.name}</p><p className="text-xs text-wp-ink-muted">Qty {it.qty}</p></div>
                  <span className="price-num text-sm font-semibold">{formatINR(it.price * it.qty)}</span>
                </li>
              ))}
            </ul>
            <div className="mt-4 space-y-2 border-t border-border pt-4">
              <div className="flex items-center justify-between text-sm">
                <span className="text-wp-ink-muted">Products</span>
                <span className="price-num font-medium" data-testid="checkout-subtotal">{formatINR(subtotal)}</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-wp-ink-muted">Shipping</span>
                <span className="price-num font-medium" data-testid="checkout-shipping">{shipping === 0 ? "Free" : formatINR(shipping)}</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-wp-ink-muted">Packaging</span>
                <span className="font-medium" data-testid="checkout-packaging-label">{packaging === "signature" ? "Signature Box" : "Standard"}</span>
              </div>
              <div className="flex items-center justify-between border-t border-border pt-2.5">
                <span className="text-sm font-semibold text-wp-ink">Total Payable</span>
                <span className="price-num text-xl font-semibold text-wp-rose-strong" data-testid="checkout-total">{formatINR(total)}</span>
              </div>
            </div>
            <p className="mt-2 text-xs text-wp-ink-muted" data-testid="checkout-shipping-note">{fillPlaceholders(content.shipping_note, { shipping_charge: formatINR(shipping) })}</p>
            <div className="mt-3 rounded-wp-md border border-wp-mint/40 bg-wp-mint/10 px-3 py-2.5 text-xs leading-relaxed text-wp-ink" data-testid="checkout-payment-value-note">
              {content.payment_value_note}
            </div>
            {belowMin && (
              <div className="mt-3 rounded-wp-md border border-wp-gold/40 bg-wp-gold/10 px-3 py-2.5 text-center text-sm text-wp-ink" data-testid="checkout-min-order-note">
                Add <span className="font-semibold text-wp-rose-strong">{formatINR(amountToMin)}</span> more {packaging === "signature" ? "to unlock Signature packaging." : "to place your order."}
              </div>
            )}
            <Button onClick={placeOrder} disabled={loading || belowMin} data-testid="checkout-whatsapp-button" className="mt-4 w-full rounded-xl bg-wp-success py-6 text-base text-white hover:brightness-105 disabled:opacity-60">
              {loading ? <Loader2 className="mr-2 h-5 w-5 animate-spin" /> : <MessageCircle className="mr-2 h-5 w-5" />} Place order via WhatsApp
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
